import React, { useState } from 'react';
import { useSelector } from "react-redux";
import { useAuthToken } from "../TokenContext";
import { NavHashLink } from 'react-router-hash-link';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Fade,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useIntl } from "react-intl";

// Constants
const MAX_INVITES = 5;
const MIN_INVITES = 1;

const Clientinvite = () => {
  const theme = useTheme();
  // Using Artemy design tokens directly for robust dark mode
  const [tabValue, setTabValue] = useState(0);
  const token = useAuthToken();
  const { user } = useSelector(state => state.auth);
  const [invites, setInvites] = useState([{ emails: '' }]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const intl = useIntl();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setInvites([{ emails: '' }]);
    setError('');
    setSuccess('');
  };

  const validateEmails = (emails) => {
    // Check for duplicates
    const emailValues = emails.map(invite => invite.emails.toLowerCase());
    const uniqueEmails = new Set(emailValues);
    if (uniqueEmails.size !== emails.length) {
      return 'Duplicate email addresses are not allowed';
    }

    // Check for valid email format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const invalidEmails = emails.filter(invite => !emailRegex.test(invite.emails));
    if (invalidEmails.length > 0) {
      return 'Please enter valid email addresses';
    }

    return null;
  };

  const handleInputChange = (index, value) => {
    const newInvites = invites.map((invite, i) =>
      i === index ? { ...invite, emails: value } : invite
    );
    setInvites(newInvites);
    setError('');
  };

  const handleAddEmail = () => {
    /* istanbul ignore next */
    if (invites.length >= MAX_INVITES) {
      setError(`Maximum ${MAX_INVITES} invites allowed`);
      return;
    }
    setInvites([...invites, { emails: '' }]);
    setError('');
  };

  const handleRemoveEmail = (index) => {
    /* istanbul ignore next */
    if (invites.length <= MIN_INVITES) {
      setError('At least one email is required');
      return;
    }
    const newInvites = invites.filter((_, i) => i !== index);
    setInvites(newInvites);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const validationError = validateEmails(invites);
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      // Choose the endpoint based on the selected tab
      const endpoint = tabValue === 0
        ? process.env.REACT_APP_CUSTOMERADMIN_EMAILINVITE // Stakeholder
        : process.env.REACT_APP_CUSTOMERADMIN_EMAILINVITEMANAGER; // Manager

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          invite: invites,
          customer_id: user.data[0].customer_id
        })
      });

      const data = await response.json();

      if (data.status === 200) {
        setSuccess('Invite emails sent successfully');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setError(data.data || 'Failed to send invites');
      }
    } catch (error) {
      setError('Failed to send invites. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if user has an active subscription
  const hasActiveSubscription = user.subscriptiondata &&
    user.subscriptiondata.length > 0 &&
    user.subscriptiondata[0].Status === 'active';

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Fade in timeout={600}>
        <Box>
          {!hasActiveSubscription ? (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: 'var(--artemy-bg-card)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '24px',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: 'var(--artemy-text-primary)'
                }}
              >
                {intl.formatMessage({ id: "noActiveSubscriptionStart" })}
              </Typography>
              <Button
                variant="contained"
                component={NavHashLink}
                to="/subscriptions"
                sx={{
                  bgcolor: '#ffc107',
                  color: '#000',
                  '&:hover': {
                    bgcolor: '#e0a800'
                  }
                }}
              >
                {intl.formatMessage({ id: "subscribe" })}
              </Button>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: '24px',
                overflow: 'hidden',
                bgcolor: 'var(--artemy-bg-card)',
                border: '1px solid var(--artemy-border)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                backdropFilter: 'blur(10px)'
              }}
            >
              {/* Navigation Tabs */}
              <Paper
                elevation={0}
                sx={{
                  bgcolor: 'var(--artemy-bg-elevated)',
                  borderRadius: 0,
                  p: 1
                }}
              >
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{
                    minHeight: '48px',
                    '& .MuiTabs-indicator': { display: 'none' },
                    '& .MuiTab-root': {
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      borderRadius: '12px',
                      color: 'var(--artemy-text-secondary)',
                      transition: 'all 0.3s ease',
                      minHeight: '48px',
                      '&:hover': {
                        color: 'var(--artemy-text-primary)',
                        bgcolor: 'rgba(255, 255, 255, 0.05)'
                      }
                    },
                    '& .Mui-selected': {
                      bgcolor: 'var(--artemy-accent) !important',
                      color: '#fff !important',
                      boxShadow: '0 4px 12px rgba(61, 127, 255, 0.3)'
                    }
                  }}
                >
                  <Tab
                    icon={<PersonIcon />}
                    label="Stakeholder"
                    iconPosition="start"
                  />
                  <Tab
                    icon={<GroupIcon />}
                    label="Manager"
                    iconPosition="start"
                  />
                </Tabs>
              </Paper>

              {/* Header Section */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, rgba(61, 127, 255, 0.15), rgba(61, 127, 255, 0.05))',
                  p: 4,
                  borderBottom: '1px solid var(--artemy-border)',
                  textAlign: 'center'
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'var(--artemy-text-primary)', fontFamily: 'var(--artemy-font-display)' }}>
                  {tabValue === 0
                    ? "Invite Stakeholder"
                    : "Invite Pod Managers"}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Send invitation emails to expand your team
                </Typography>
              </Box>

              {/* Form Section */}
              <Box sx={{ p: 3 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Box sx={{ mb: 3 }}>
                    {invites.map((invite, index) => (
                      <Card
                        key={index}
                        elevation={0}
                        sx={{
                          mb: 2,
                          bgcolor: 'var(--artemy-bg-elevated)',
                          border: '1px solid var(--artemy-border)',
                          borderRadius: '16px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: 'var(--artemy-accent)',
                            boxShadow: '0 4px 20px rgba(61, 127, 255, 0.15)'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <EmailIcon
                              sx={{
                                color: 'var(--artemy-accent)',
                                fontSize: '1.5rem'
                              }}
                            />
                            <TextField
                              fullWidth
                              type="email"
                              placeholder="Enter an Email"
                              value={invite.emails}
                              onChange={(e) => handleInputChange(index, e.target.value)}
                              required
                              variant="standard"
                              error={Boolean(error && invite.emails === '')}
                              InputProps={{
                                disableUnderline: true,
                                sx: {
                                  color: 'var(--artemy-text-primary)',
                                  fontFamily: 'var(--artemy-font-body)',
                                  fontSize: '1rem',
                                  py: 1
                                }
                              }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {index === invites.length - 1 && invites.length < MAX_INVITES && (
                                <Tooltip title="Add another email">
                                  <IconButton
                                    onClick={handleAddEmail}
                                    sx={{
                                      bgcolor: 'rgba(46, 213, 115, 0.15)',
                                      color: '#2ed573',
                                      '&:hover': {
                                        bgcolor: 'rgba(46, 213, 115, 0.25)'
                                      }
                                    }}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {invites.length > MIN_INVITES && (
                                <Tooltip title="Remove this email">
                                  <IconButton
                                    onClick={() => handleRemoveEmail(index)}
                                    sx={{
                                      bgcolor: 'rgba(255, 71, 87, 0.15)',
                                      color: '#ff4757',
                                      '&:hover': {
                                        bgcolor: 'rgba(255, 71, 87, 0.25)'
                                      }
                                    }}
                                  >
                                    <RemoveIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  {/* Submit Button */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? null : <SendIcon />}
                      sx={{
                        minWidth: 200,
                        py: 2,
                        borderRadius: '12px',
                        fontWeight: 700,
                        fontSize: '1rem',
                        background: 'var(--artemy-accent-gradient)',
                        color: '#fff !important',
                        boxShadow: '0 8px 24px rgba(61, 127, 255, 0.25)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 12px 32px rgba(61, 127, 255, 0.35)',
                          transform: 'translateY(-2px)'
                        },
                        '&:disabled': {
                          bgcolor: 'grey.400'
                        }
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : 'Invite'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default Clientinvite;